"use client";

import React, { useState, useRef, useCallback } from "react";
import { Currency } from "@/types";
import { PercentageWhiteCircleIcon } from "@/shared/ui/icons/percentage-white-circle-icon";
import { IneligibleLock } from "@/shared/ui/icons/ineligible-lock";

interface PaymentOptionSelectProps {
  selectedPayments: number;
  onPaymentSelectionChange: (payments: number) => void;
  paymentOptions: {
    payments: number;
    noFees: boolean;
    isDeclined?: boolean;
  }[];
  subventionData?: Record<
    number,
    { amount: number; currency: Currency | string }
  >;
  /** Tenure numbers (payments) that show the green subvention checkmark. */
  subventionEligibleTenures?: Set<number>;
  currency?: Currency | string;
  language?: string;
  waivedOffFeeData?: Record<number, { amount: number; reason?: string | null }>;
  disabled?: boolean;
}

function getOptionButtonProps(
  disabled: boolean,
  isSelected: boolean,
  isDragging: boolean,
): { className: string; style: React.CSSProperties } {
  const className = [
    "relative flex items-center justify-center flex-1 h-full text-lg font-medium transition-all duration-300 ease-out z-10",
    disabled
      ? "text-[#585B7A] cursor-not-allowed"
      : isSelected
        ? `text-purple-600 scale-105 ${isDragging ? "cursor-grabbing" : "cursor-grab"}`
        : "text-[#585B7A] hover:text-gray-800 hover:scale-105 cursor-pointer",
  ].join(" ");

  const style: React.CSSProperties = {
    transition: "color 0.3s ease, transform 0.2s ease",
    ...(isSelected && !isDragging && { transform: "scale(1.05)" }),
  };

  return { className, style };
}

const PaymentOptionSelect = ({
  selectedPayments,
  onPaymentSelectionChange,
  paymentOptions,
  subventionEligibleTenures,
  disabled = false,
}: PaymentOptionSelectProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const isRTL =
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("dir") === "rtl";

  const handleActiveTabMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
  };

  const handleActiveTabTouchStart = (e: React.TouchEvent) => {
    if (disabled) return;
    e.stopPropagation();
    setIsDragging(true);
    setDragStartX(e.touches[0].clientX);
    setDragOffset(0);
  };

  const handleGlobalMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      const offset = e.clientX - dragStartX;
      setDragOffset(offset);
    },
    [isDragging, dragStartX],
  );

  const handleGlobalTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      const offset = e.touches[0].clientX - dragStartX;
      setDragOffset(offset);
    },
    [isDragging, dragStartX],
  );

  const handleGlobalMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const containerWidth = containerRef.current?.offsetWidth || 0;
    const tabWidth =
      paymentOptions.length !== 0 ? containerWidth / paymentOptions.length : 0;
    const currentIndex = paymentOptions.findIndex(
      (option) => option.payments === selectedPayments,
    );

    // Calculate which tab position the dragged element is closest to (visual index from left)
    const visualCurrentIndex = isRTL
      ? paymentOptions.length - 1 - currentIndex
      : currentIndex;
    const currentTabPosition = visualCurrentIndex * tabWidth;
    const draggedPosition = currentTabPosition + dragOffset;

    // Find the closest visual tab based on the dragged position
    let newVisualIndex = Math.round(draggedPosition / tabWidth);

    // Clamp to valid range
    newVisualIndex = Math.max(
      0,
      Math.min(newVisualIndex, paymentOptions.length - 1),
    );

    const newIndex = isRTL
      ? paymentOptions.length - 1 - newVisualIndex
      : newVisualIndex;
    const newPayments = paymentOptions[newIndex].payments;
    onPaymentSelectionChange(newPayments);
    setDragOffset(0);
  }, [
    isDragging,
    paymentOptions,
    selectedPayments,
    dragOffset,
    onPaymentSelectionChange,
    isRTL,
  ]);

  const handleGlobalTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const containerWidth = containerRef.current?.offsetWidth || 0;
    const tabWidth =
      paymentOptions.length !== 0 ? containerWidth / paymentOptions.length : 0;
    const currentIndex = paymentOptions.findIndex(
      (option) => option.payments === selectedPayments,
    );

    // Calculate which tab position the dragged element is closest to (visual index from left)
    const visualCurrentIndex = isRTL
      ? paymentOptions.length - 1 - currentIndex
      : currentIndex;
    const currentTabPosition = visualCurrentIndex * tabWidth;
    const draggedPosition = currentTabPosition + dragOffset;

    // Find the closest visual tab based on the dragged position
    let newVisualIndex = Math.round(draggedPosition / tabWidth);

    // Clamp to valid range
    newVisualIndex = Math.max(
      0,
      Math.min(newVisualIndex, paymentOptions.length - 1),
    );

    const newIndex = isRTL
      ? paymentOptions.length - 1 - newVisualIndex
      : newVisualIndex;
    const newPayments = paymentOptions[newIndex].payments;
    onPaymentSelectionChange(newPayments);
    setDragOffset(0);
  }, [
    isDragging,
    paymentOptions,
    selectedPayments,
    dragOffset,
    onPaymentSelectionChange,
    isRTL,
  ]);

  // Add global event listeners for drag
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.addEventListener("touchmove", handleGlobalTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleGlobalTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
        document.removeEventListener("touchmove", handleGlobalTouchMove);
        document.removeEventListener("touchend", handleGlobalTouchEnd);
      };
    }
  }, [
    isDragging,
    dragStartX,
    dragOffset,
    handleGlobalMouseMove,
    handleGlobalMouseUp,
    handleGlobalTouchMove,
    handleGlobalTouchEnd,
  ]);

  const eligibleSet = subventionEligibleTenures ?? new Set<number>();

  const pillIndex = paymentOptions.findIndex(
    (option) => option.payments === selectedPayments,
  );
  const pillVisualIdx =
    pillIndex >= 0
      ? isRTL
        ? paymentOptions.length - 1 - pillIndex
        : pillIndex
      : 0;
  const optionCount = paymentOptions?.length ?? 1;

  const pillStyle = React.useMemo(() => {
    const gap = 8;
    const slotSize = `(100% - ${gap}px) / ${optionCount}`;
    return {
      width: `calc(${slotSize})`,
      left: `calc(${gap / 2}px + ${pillVisualIdx} * ${slotSize})`,
      transform: `translateX(${isDragging ? dragOffset : 0}px)`,
    } as React.CSSProperties;
  }, [optionCount, pillVisualIdx, isDragging, dragOffset]);

  return (
    <div className={`relative ${disabled ? "pointer-events-none" : ""}`}>
      <div
        ref={containerRef}
        className="bg-[#EBEEF4] p-1 rounded-full flex items-center relative overflow-hidden select-none h-[56px]"
      >
        {/* Animated background pill */}
        <div
          className={`absolute top-1 bg-white rounded-full shadow-sm h-[calc(100%-8px)] ${
            isDragging
              ? "transition-none"
              : "transition-all duration-300 ease-out"
          }`}
          style={pillStyle}
        />
        {paymentOptions.map((option) => {
          const isSelected = selectedPayments === option.payments;
          const { className: buttonClassName, style: buttonStyle } =
            getOptionButtonProps(disabled, isSelected, isDragging);

          return (
            <button
              key={option.payments}
              onClick={() =>
                !disabled && onPaymentSelectionChange(option.payments)
              }
              onMouseDown={isSelected ? handleActiveTabMouseDown : undefined}
              onTouchStart={isSelected ? handleActiveTabTouchStart : undefined}
              disabled={disabled}
              className={buttonClassName}
              style={buttonStyle}
            >
              {option.isDeclined && (
                <IneligibleLock
                  className="w-[1em] h-[1em] shrink-0 mr-1 rtl:mr-0 rtl:ml-1"
                  aria-hidden
                  data-testid="ineligible-lock"
                />
              )}
              {eligibleSet.has(option.payments) && (
                <PercentageWhiteCircleIcon
                  className="w-3 h-3 shrink-0 mr-1 rtl:mr-0 rtl:ml-1 [&_path]:fill-[#128351]"
                  aria-label="Subvention eligible"
                />
              )}
              {option.payments}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentOptionSelect;
